import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { useCallback, useState } from 'react';

import type { RfidCardType } from '@/api/schema';
import CardSvg from '@/components/ui/svg/CardSvg';
import CardDetails from '@/features/administration/cards/components/CardDetails';
import { t } from '@/lib/utils';

interface ChargingCardSidebarProps {
    open: boolean;
    dismiss: (reloadCards: boolean) => void;
    card?: RfidCardType;
}

const ChargingCardSidebar = ({ open, dismiss, card }: ChargingCardSidebarProps) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [hasPendingChanges, setHasPendingChanges] = useState(false);

    const resetState = useCallback(() => {
        setIsDialogOpen(false);
        setHasPendingChanges(false);
    }, []);

    const handleClose = useCallback(() => {
        if (hasPendingChanges) {
            setIsDialogOpen(true);
        } else {
            dismiss(false);
            resetState();
        }
    }, [hasPendingChanges, dismiss, resetState]);

    const handleDismiss = () => {
        dismiss(false);
        resetState();
    };

    const handleSubmit = () => {
        dismiss(true);
        resetState();
    };

    const handleEdit = () => {
        setIsDialogOpen(false);
    };

    const header = (
        <div className='margin-bottom-25 margin-top-5 padding-left-5'>
            {t('chargingCards.chargingCardDetail')}
            <div className='display-flex align-items-center gap-10 margin-top-15'>
                <CardSvg />
                <span>{card?.number}</span>
            </div>
        </div>
    );

    return (
        <Sidebar
            showHeaderBorder
            title={header}
            closed={!open}
            onClose={handleClose}
            bodyClassName='padding-x-15 margin-top-25'
            position={Sidebar.RIGHT}
            hasBackdrop={true}
            onBackdropClick={handleClose}
            width={450}
            fly
        >
            {open && (
                <CardDetails
                    onSubmit={handleSubmit}
                    onClose={handleDismiss}
                    setAbort={setIsDialogOpen}
                    onDirtyChange={setHasPendingChanges}
                    state={card}
                />
            )}

            <Dialog
                show={isDialogOpen}
                title={<span>{t('general.pendingChanges.title')}</span>}
                body={<span>{t('general.pendingChanges.description')}</span>}
                footer={
                    <div className='btn-toolbar justify-content-end'>
                        <Button onClick={handleEdit}>{t('general.button.keepEditing')}</Button>
                        <Button onClick={handleDismiss} bsStyle='primary'>
                            {t('general.button.discardChanges')}
                        </Button>
                    </div>
                }
                className='rifdCardDialog'
                showCloseButton={false}
                bsSize='md'
            />
        </Sidebar>
    );
};

export default ChargingCardSidebar;
